/**
 * Trade policy / execution rules simulator — filter_catalog (e.g. v10) + policy_tree.v1.
 * Simulate POST body: { policy: { action, root } } only (no legacy combine/rules).
 */

export type TradePolicyValueKind = "enum" | "number";

export type TradePolicyAction = "veto" | "keep_only";

export type TradePolicyGroupOp = "and" | "or";

export type TradePolicyRuleOp = "eq" | "in" | "gte" | "lte" | "ne";

export interface TradePolicyCatalogField {
  path: string;
  label: string;
  /** enum: string rules (eq / in / ne). number: JSON number (eq / gte / lte / ne). */
  value_kind: TradePolicyValueKind;
  /** Drive operator dropdown from this; never offer gte/lte on enum paths. */
  allowed_ops: string[];
  /** For value_kind enum: allowed tokens (typically lowercase); FE matches case-insensitively. */
  enum_values?: string[];
  description?: string;
}

export interface TradePolicyTreeLimits {
  max_depth?: number;
  max_nodes?: number;
}

/** Metadata from filter-catalog for builder limits and templates. */
export interface TradePolicyTreeMeta {
  node_kinds?: string[];
  group_ops?: string[];
  rule_ops?: string[];
  limits?: TradePolicyTreeLimits;
  /** Example policy tree or full policy — shape may vary; FE tries root + action. */
  example?: unknown;
}

/** Marginal bucket strings like `session:london_new_york` → catalog `eq` rules (filter_catalog.v10+). */
export interface TradePolicyMarginalBucketImport {
  /** Map from clause / bucket prefix before first `:` to catalog field path (e.g. `session` → `session_time.session_id`). */
  eq_string_prefixes?: Record<string, string>;
}

export interface TradePolicyFilterCatalogResponse {
  version: string;
  policy_schema_version?: string;
  fields: TradePolicyCatalogField[];
  policy_tree?: TradePolicyTreeMeta;
  marginal_bucket_import?: TradePolicyMarginalBucketImport;
}

export interface TradePolicyRuleNode {
  kind: "rule";
  path: string;
  op: TradePolicyRuleOp;
  value: string | number | string[];
}

export interface TradePolicyGroupNode {
  kind: "group";
  op: TradePolicyGroupOp;
  children: TradePolicyPolicyNode[];
}

export type TradePolicyPolicyNode = TradePolicyRuleNode | TradePolicyGroupNode;

export interface TradePolicyPolicyPayload {
  action: TradePolicyAction;
  root: TradePolicyPolicyNode;
}

/** Canonical bundle segment kinds from filter-catalog `policy_tree_bundle.segment_kinds`. */
export type PolicyTreeBundleSegmentKind = "entry_filter" | "management";

/** One veto/keep-only segment inside a combined simulator policy. */
export interface PolicyTreeBundleSegmentV1 {
  action: TradePolicyAction;
  root: TradePolicyPolicyNode;
  /** Use `entry_filter` for regime/entry picks; `management` for management explorer rules. Omit to infer entry_filter. */
  segment_kind?: PolicyTreeBundleSegmentKind;
  /** FE display label only (e.g. pick name); not a bundle segment kind. */
  source_title?: string;
  /** Strategy policy version this segment was copied from (combine / save). */
  source_policy_version_id?: string;
  source_policy_version_number?: number;
  /**
   * Candle TF this segment evaluates on (combine / multi-TF entry).
   * Budget gate uses the finest of these across the bundle.
   */
  candle_timeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d" | string;
}

/** Bundle-level combine metadata written by strategy policy combine. */
export type PolicyTreeBundleMetaV1 = {
  combined_at?: string;
  segment_count?: number;
  /** How keep-only segments combine (`any` = OR, `all` = AND). */
  entry_keep_combine?: "all" | "any";
  /** How remove (veto) segments combine (`any` = OR, `all` = AND). */
  entry_veto_combine?: "all" | "any";
  /** Finest candle TF across source segments (multi-TF combine). */
  resolved_candle_timeframe?: string;
  /** Distinct source segment candle TFs (multi-TF combine). */
  source_candle_timeframes?: string[];
  /** Dual-TF: management / Exit Placement replay TF. */
  management_candle_timeframe?: string;
  /** Dual-TF: entry-filter TF when distinct from management. */
  entry_candle_timeframe?: string;
  /** EP-only / combine stamp of management TF (also on segments). */
  candle_timeframe?: string;
  /** Chip set stamped on combine / persist (Keep / Remove / SL / TP). */
  policy_kinds?: Array<"keep" | "remove" | "sl" | "tp">;
  policyKinds?: Array<"keep" | "remove" | "sl" | "tp">;
  entry_actions?: Array<"keep_only" | "veto">;
  entryActions?: Array<"keep_only" | "veto">;
  exit_legs?: "none" | "sl" | "tp" | "both";
  exitLegs?: "none" | "sl" | "tp" | "both";
};

/** Mixed veto + keep-only segments (same shape as strategy policy combine). */
export interface PolicyTreeBundleV1 {
  schema_version: "policy_tree.bundle.v1";
  segments: PolicyTreeBundleSegmentV1[];
  /** BE combine result stores operands here (preferred). */
  meta?: PolicyTreeBundleMetaV1;
  /** Legacy / FE write path — prefer `meta.entry_keep_combine`. */
  entry_keep_combine?: "all" | "any";
  /** Legacy / FE write path — prefer `meta.entry_veto_combine`. */
  entry_veto_combine?: "all" | "any";
}

/** policy_tree.v2 management action params (BE §7). */
export type PolicyTreeV2ActionType =
  | "sl_distance_override"
  | "tp_distance_override"
  | "breakeven_move"
  | "trailing_enable"
  | "trailing_activation"
  | "trailing_update"
  | "time_stop"
  | string;

export interface PolicyTreeV2ManagementAction {
  type: PolicyTreeV2ActionType;
  params?: Record<string, unknown>;
}

export interface PolicyTreeV2Rule {
  id?: string;
  enabled?: boolean;
  condition: TradePolicyPolicyNode;
  actions: PolicyTreeV2ManagementAction[];
  /** FE bookkeeping when a rule is detached from an explorer pick preset. */
  meta?: {
    exit_placement_source_rule_id?: string;
    exit_placement_slice_id?: string;
  };
}

/** Exit Placement replay metadata echoed on saved backtests / simulate payloads. */
export interface ExitPlacementReplayRuleConfig {
  simulate_legs?: string;
  baseline_distance_model?: {
    sl_distance_atr?: number;
    tp_distance_atr?: number;
  };
}

export interface ExitPlacementReplayConfig {
  by_rule_id?: Record<string, ExitPlacementReplayRuleConfig>;
}

export interface PolicyTreeV2 {
  schema_version: "policy_tree.v2" | string;
  rules: PolicyTreeV2Rule[];
  exit_placement_replay?: ExitPlacementReplayConfig;
}

export type TradePolicySimulatePolicy = TradePolicyPolicyPayload | PolicyTreeBundleV1 | PolicyTreeV2;

export function isPolicyTreeBundleV1(policy: unknown): policy is PolicyTreeBundleV1 {
  return (
    typeof policy === "object" &&
    policy !== null &&
    (policy as PolicyTreeBundleV1).schema_version === "policy_tree.bundle.v1" &&
    Array.isArray((policy as PolicyTreeBundleV1).segments)
  );
}

export function isPolicyTreeV2(policy: unknown): policy is PolicyTreeV2 {
  if (typeof policy !== "object" || policy === null) return false;
  if (isPolicyTreeBundleV1(policy)) return false;
  const p = policy as PolicyTreeV2;
  if (p.schema_version === "policy_tree.v2") {
    return Array.isArray(p.rules);
  }
  if (!Array.isArray(p.rules) || p.rules.length === 0) return false;
  return p.rules.every(
    (rule) =>
      rule != null &&
      typeof rule === "object" &&
      "condition" in rule &&
      Array.isArray((rule as PolicyTreeV2Rule).actions)
  );
}

/** policy_tree.v2 from simulate POST/poll bodies (`policy_tree` alias or canonical `policy`). */
export function extractSimulatePolicyTreeV2(body: unknown): PolicyTreeV2 | null {
  if (!body || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  const tree = record.policy_tree ?? record.policy;
  if (!isPolicyTreeV2(tree)) return null;
  if (!Array.isArray(tree.rules) || tree.rules.length === 0) return null;
  return tree;
}

export function simulatePolicySchemaVersion(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const record = body as Record<string, unknown>;
  const fromField =
    typeof record.policy_schema_version === "string" ? record.policy_schema_version.trim() : "";
  if (fromField) return fromField;
  const tree = extractSimulatePolicyTreeV2(body);
  const fromTree =
    tree && typeof tree.schema_version === "string" ? tree.schema_version.trim() : "";
  return fromTree || undefined;
}

export function isPolicyPayloadV1(policy: unknown): policy is TradePolicyPolicyPayload {
  if (isPolicyTreeV2(policy) || isPolicyTreeBundleV1(policy)) return false;
  return (
    typeof policy === "object" &&
    policy !== null &&
    "action" in policy &&
    "root" in policy
  );
}

export interface TradePolicySimulateRequest {
  policy: TradePolicySimulatePolicy;
  /** Optional: select which policy-row feature schema to use (e.g. policy_row.v1 vs policy_row.pine_v1). */
  policy_row_version?: string;
  /**
   * Candle timeframe used to compute market_context/rows for simulation.
   * Default behavior: omit or send "1m". Locked policy runs should send explicitly (including 1m).
   */
  candle_timeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  /** Dual-TF: entry filters evaluate on this TF when set. */
  entry_candle_timeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  /** Dual-TF: exit-placement / management replay TF when set. */
  management_candle_timeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  /** Optional: control whether simulator fills missing OHLC on demand. Prefer false for determinism. */
  simulate_ohlc_fill_missing?: boolean;
  broker_account_code?: string;
  start_date?: string;
  end_date?: string;
  timeframe?: string;
  local_start_date?: string;
  local_end_date?: string;
  timezone?: string;
  reporting_timezone?: string;
  symbol?: string;
  include_trade_ids?: boolean;
  limit?: number;
  /** Optional: when true, backend marks this job as eligible for explicit save (POST .../jobs/{job_id}/save). */
  save_backtest?: boolean;
  /** Optional: link saved simulation to a report UUID. */
  report_id?: string;
  /** Optional: associate this simulation with a saved strategy (for policy version save). */
  strategy_id?: string;
  /** Optional: simulation title (UI label). */
  title?: string;
  /** Cohort filter — must match report when simulating from report context. */
  trade_side_filter?: "both" | "long" | "short";
}

export type TradePolicyMetric = {
  trade_count?: number;
  win_count?: number;
  loss_count?: number;
  win_rate?: number;
  total_pnl?: number;
  expectancy?: number;
  profit_factor?: number;
  max_drawdown?: number;
  max_drawdown_percent?: number;
  /** Present on `changed` cohort (Exit Placement / management). */
  total_pnl_delta?: number;
  baseline_total_pnl?: number;
  baseline_win_rate?: number;
  baseline_expectancy?: number;
} & Record<string, unknown>;

export interface TradePolicyCoverage {
  trades_total?: number;
  trades_with_entry_bar?: number;
  trades_missing_entry_bar?: number;
}

/** Aggregate delta on management / Exit Placement sims (and hybrids with management). */
export interface TradePolicySimulateDelta {
  /** filtered.trade_count − baseline.trade_count. RA often negative; pure EP usually 0. */
  trade_count?: number;
  /** Trades whose sim changed PnL or exit_reason. Prefer for Exit Placement cards. Equals `changed.trade_count`. */
  trades_changed?: number;
  /** filtered.total_pnl − baseline.total_pnl (same meaning as before). */
  total_pnl?: number;
}

/** Which cohort column(s) to bind after baseline/filtered. */
export type TradePolicyComparisonLane = "removed" | "changed" | "both";

export interface TradePolicySimulateMeta {
  timeframe?: string;
  candle_timeframe?: string;
  start_date?: string;
  end_date?: string;
  symbol?: string;
  broker_account_code?: string;
  action?: TradePolicyAction;
  policy_node_count?: number;
  policy_schema_version?: string;
  policy_row_version?: string;
  entry_window_schema?: string;
  /**
   * Which cohort columns to show:
   * - `removed` | `changed` → one column from `result[lane]`
   * - `both` → Removed and Changed columns (hybrid entry+management)
   */
  comparison_lane?: TradePolicyComparisonLane;
  [key: string]: unknown;
}

export interface TradePolicySimulateResponse {
  baseline?: TradePolicyMetric;
  filtered?: TradePolicyMetric;
  /** Trades left out by keep/veto — same metric shape as baseline/filtered. */
  removed?: TradePolicyMetric;
  /** Trades whose exit changed (EP/management) — same metric shape; may include baseline_* extras. */
  changed?: TradePolicyMetric;
  delta?: TradePolicySimulateDelta;
  coverage?: TradePolicyCoverage;
  meta?: TradePolicySimulateMeta;
  kept_trade_ids?: string[];
  removed_trade_ids?: string[];
  /** When returned, a simulation job was created even if a result payload is included immediately. */
  job_id?: string;
  simulated_trades?: Array<Record<string, unknown>>;
  baseline_trades?: Array<Record<string, unknown>>;
}

export type TradePolicySimulateJobStatus = "queued" | "running" | "succeeded" | "failed";

export type TradePolicySimulateJobStartResponse = {
  job_id: string;
  status: "queued" | "running";
  reused_existing?: boolean;
  queued_at?: string;
};

export type TradePolicySimulateJobEnvelope = {
  job_id: string;
  status: TradePolicySimulateJobStatus;
  progress?: Record<string, unknown>;
  result?: TradePolicySimulateResponse;
  error?: string;
  reused_existing?: boolean;
  queued_at?: string;
  started_at?: string;
  finished_at?: string;
  backtest_id?: string;
  backtest_summary?: import("./backtests").BacktestSummary;
  /** Full policy on poll payload (alias of request.policy for v2 exit-placement). */
  policy?: PolicyTreeV2 | TradePolicySimulatePolicy;
  policy_tree?: PolicyTreeV2 | TradePolicySimulatePolicy;
  policy_schema_version?: string;
  request?: { policy?: unknown; [key: string]: unknown };
};

/** Completed simulate job — result plus optional saved backtest metadata. */
export type TradePolicySimulateJobOutcome = {
  result: TradePolicySimulateResponse;
  backtestId?: string;
  backtestSummary?: import("./backtests").BacktestSummary;
  policyTree?: PolicyTreeV2;
  policySchemaVersion?: string;
};

export interface TradePolicySimScopeInputs {
  action?: TradePolicyAction;
  use_custom_range?: boolean;
  timeframe?: string;
  local_start_date?: string | null;
  local_end_date?: string | null;
  timezone?: string;
  symbol?: string;
  segment_key?: string;
  /** Exit Placement explorer slice ids (e.g. by_market_condition:expansion). */
  slice_ids?: string[];
  /** Exit Placement replay legs requested for this sim config. */
  simulate_legs?: "both" | "sl_only" | "tp_only";
  include_trade_ids?: boolean | string[];
  limit?: number | null;
  candle_timeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h";
  policy_row_version?: "policy_row.v1" | "policy_row.pine_v1";
  trade_side_filter?: "both" | "long" | "short";
}

export interface TradePolicySimViewState {
  mode?: "builder" | "graph";
  collapsed_group_ids?: string[];
}

export interface TradePolicySimConfigRecord {
  report_id: string;
  policy_tree: unknown;
  scope_inputs?: TradePolicySimScopeInputs | null;
  view_state?: TradePolicySimViewState | null;
  policy_schema_version?: string | null;
  group_count?: number | null;
  rule_count?: number | null;
  revision: number;
  updated_at?: string;
  linked_backtest_id?: string | null;
  linked_job_id?: string | null;
  simulation_overlay_mode?: string | null;
}

export interface TradePolicySimConfigUpsertRequest {
  policy_tree: unknown;
  scope_inputs?: TradePolicySimScopeInputs;
  view_state?: TradePolicySimViewState;
  policy_schema_version?: string;
  group_count?: number;
  rule_count?: number;
  revision: number;
  linked_backtest_id?: string;
  linked_job_id?: string;
  simulation_overlay_mode?: string;
}

/** GET /v1/trade-policy/simulate/candle-budget-preview — no coarsen. */
export type SimulateCandleBudgetPreviewQuery = {
  symbol: string;
  candle_timeframe: string;
  broker_account_code?: string;
  start_date?: string;
  end_date?: string;
  timeframe?: string;
  local_start_date?: string;
  local_end_date?: string;
  timezone?: string;
  report_id?: string;
  entry_candle_timeframe?: string;
  management_candle_timeframe?: string;
};

export type SimulateCandleBudgetSuggestedPeriod = {
  window_start: string;
  window_end: string;
  bar_count?: number;
  trade_count?: number;
  is_current?: boolean;
  label?: string;
};

export type SimulateCandleBudgetPreviewResponse = {
  max_bars: number;
  requested_candle_timeframe: string;
  resolved_candle_timeframe: string;
  will_coarsen: boolean;
  analysis_bar_count: number;
  exceeds_budget: boolean;
  max_calendar_days?: number;
  suggested_periods?: SimulateCandleBudgetSuggestedPeriod[];
  message?: string;
};

/** Stable simulate reject when window × TF exceeds the OHLC bar cap. */
export const CANDLE_BAR_BUDGET_EXCEEDED_CODE = "candle_bar_budget_exceeded" as const;
